import axios from 'smg/axios';
import * as blockchyp from 'smg/blockchyp';
import * as models from 'smg/customer-api/models';
import { DeletePaymentOption, NewPaymentOption, PaymentMerchant } from 'smg/customer-api/paymentoptions';
import { ShowError } from 'smg/errors';
import * as phone from 'smg/phone';
import * as toast from 'smg/toast';

import { CardTokenFields, TokenizedToFields } from '../../blockchyp-card';

import { State } from '../state';
import type { CheckoutWindow, Payment } from '../types';

declare const window: CheckoutWindow;

// CardFormScope is one card form: the payment it writes, the BlockChyp
// mount it owns, and the element field errors paint inside.
export interface CardFormScope {
    payment: Payment;
    getMount: () => blockchyp.Mount | undefined;
    setMount: (mount: blockchyp.Mount | undefined) => void;
    root: ParentNode;
}

export function SingleCardScope(state: State): CardFormScope {
    return {
        payment: state.payment,
        getMount: () => state.creditCardMount,
        setMount: mount => {
            state.creditCardMount = mount;
        },
        root: state.dom.$paymentBody ?? document,
    };
}

// PaymentOptions fetches a page of the user's saved credit-card/bank
// payment options, ordered by most recently used.
export async function PaymentOptions(page?: number) {
    const params = {
        'q': 'valid:true has:email has:phone has:fullName has:addressLine1 has:city has:countryID has:zip' +
            ` paymentOptionTypeID:${models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT},${models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK}`,
        'data': 'address,' +
            'creditCardFirst6,creditCardLast4,creditCardExpirationDate,key,paymentOptionTypeID,bankType,accountType,lastOrdered',
        'sort': 'lastOrdered desc,created desc',
        'p': page?.toString() || '0',
        'max': '5',
    };
    const resp = await axios.get('/payment-options', { params });
    const data = models.PaymentOptionsHyejooResponse.fromJSON(resp.data);
    // Skip saved options whose stored phone fails the server tel validator, which would re-trap the customer at checkout.
    data.paymentOptions = data.paymentOptions.filter(paymentOption => phone.IsValid(paymentOption.address.phoneNumber?.phone));
    return data;
}

// PendingPaymentOptionID is the client-side id a staged (not yet persisted)
// payment option carries so the saved-card list can render and edit/delete it
// before the order exists (#8111). PersistPendingPaymentOption swaps in the
// server option (real id) at submit, so this value never reaches the API.
export const PendingPaymentOptionID = 'pending-payment-option';

// CreatePaymentOption tokenizes the in-progress card through the BlockChyp
// iframe (PCI SAQ A — only the token + first6/last4 leave the page, never the
// PAN) and stages the fields for NewPaymentOption. Persistence / Stax merchant
// exchange is deferred until submit after the order exists so the token is
// minted under the order brand (#8111). Caller must have already filled
// state.payment.billingAddress and the type-specific fields (creditCard
// expiry, etc.) as appropriate. scope is the single payment or one split row.
export async function CreatePaymentOption(state: State, scope: CardFormScope = SingleCardScope(state)) {
    const p = scope.payment;
    const address = p.billingAddress?.get();
    const paymentOption: models.IPaymentOption = {
        paymentOptionTypeID: p.paymentOptionTypeID,
        fullName: address?.fullName,
        company: address?.organization,
        country: address?.country,
        countryID: address?.countryID,
        addressLine1: address?.addressLine1,
        addressLine2: address?.addressLine2,
        city: address?.city,
        state: address?.state,
        stateID: address?.stateID,
        zip: address?.postalCode,
        email: address?.emailAddress?.email,
        phone: address?.phoneNumber?.phone,
    };

    let cardTokenFields: CardTokenFields | undefined;
    if (p.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT) {
        const mount = scope.getMount();
        if (!mount) {
            throw new Error('The card form is still loading. Please re-enter your card and try again.');
        }
        // Tokenize before the order so a bad card fails without orphaning one.
        ClearPlusCardFieldInvalid(scope.root);
        p.pendingMerchantToken = await mount.webPaymentsToken.catch(() => undefined);
        try {
            const tokenized = await mount.tokenize({
                fullName: address?.fullName ?? '',
                month: p.creditCard?.expireMonth ?? 0,
                year: p.creditCard?.expireYear ?? 0,
                addressLine1: address?.addressLine1,
                addressLine2: address?.addressLine2,
                city: address?.city,
                stateCode: address?.state?.stateCode,
                zip: address?.postalCode,
                countryCode3: address?.country?.countryCode3,
                company: address?.organization,
            });
            cardTokenFields = TokenizedToFields(tokenized);
        } catch (err) {
            // Paint via the validity mirror so Plus SCSS (._blockchyp.scss)
            // turns the mount red and sizes the feedback pill — no new SCSS.
            if (err instanceof blockchyp.TokenizeFieldError) {
                MarkPlusCardFieldInvalid(err.field, err.message, scope.root);
            }
            throw err;
        }
    }

    p.pendingPaymentOptionCreate = {
        paymentOption,
        paymentToken: cardTokenFields?.paymentToken,
        creditCardFirst6: cardTokenFields?.creditCardFirst6,
        creditCardLast4: cardTokenFields?.creditCardLast4,
        expireMonth: p.creditCard?.expireMonth,
        expireYear: p.creditCard?.expireYear,
    };
    // Local so the payment step can mark complete and the summary can show a
    // selection; NewPaymentOption runs at submit. The placeholder id keeps a
    // staged card selectable in the saved-card list, and first6/last4 let the
    // summary name it before it is persisted.
    p.paymentOption = new models.PaymentOption({
        paymentOptionTypeID: paymentOption.paymentOptionTypeID,
        address,
        id: PendingPaymentOptionID,
        creditCardFirst6: cardTokenFields?.creditCardFirst6,
        creditCardLast4: cardTokenFields?.creditCardLast4,
    });
    scope.getMount()?.unload();
    scope.setMount(undefined);
    return p.paymentOption;
}

// Persist a staged new payment option under the order brand's Stax merchant
// once the order exists (#8111). No-op when a saved option is already selected.
export async function PersistPendingPaymentOption(state: State, orderID: string, scope: CardFormScope = SingleCardScope(state)): Promise<void> {
    const pending = scope.payment.pendingPaymentOptionCreate;
    if (!pending) {
        return;
    }

    // normalizeNewOrderRealmToSalesTeam (#7537) can re-home a *new* order onto
    // the sales team's primary realm when it doesn't serve the cart's realm.
    // BlockChyp's tokenizing key is per-merchant, so a token minted under the
    // cart's merchant cannot be exchanged under a different order merchant.
    // Compare the merchant this card's mount was built for (recorded on the
    // payment when it was tokenized) against the order's own merchant now, and
    // fail early rather than let the Stax exchange below fail with an opaque
    // mismatch. Fail CLOSED on missing provenance: a pending token always came
    // from a mount, so an undefined recorded merchant means the provenance was
    // lost, not "never mounted" — treat it exactly like a mismatch.
    if (pending.paymentToken) {
        const orderMerchant = await PaymentMerchant({ orderIDs: [orderID] });
        const mountToken = scope.payment.pendingMerchantToken;
        if (orderMerchant.webPaymentsToken && orderMerchant.webPaymentsToken !== mountToken) {
            // Clear the cache so the next mount (opened by the payment
            // section's own retry path below) re-resolves by orderIDs now
            // that orderResp is set, instead of remounting under the stale
            // cart-merchant promise.
            state.merchantToken = undefined;
            delete scope.payment.pendingPaymentOptionCreate;
            delete scope.payment.pendingMerchantToken;
            // Clearing paymentOption (not just pendingPaymentOptionCreate) is
            // what makes initPayment's existing "in-progress, unsaved payment"
            // branch (checkout/payment/index.ts initPayment: `if
            // (state.payment.paymentOptionTypeID && !state.payment.paymentOption)`)
            // re-render a fresh credit-card form via PromptExistingPaymentOption
            // when handleOrderPaymentError below reopens the payment
            // accordion — no new remount code needed here.
            scope.payment.paymentOption = undefined;
            throw new Error('Your order was placed, but the card needs to be re-entered to complete payment. Please re-enter your card details and click Place Order again.');
        }
    }

    const resp = await NewPaymentOption({
        user: await state.getUser(),
        fattmerchantOmniWebPaymentsToken: window.staxOmniWebPaymentsToken,
        paymentOption: pending.paymentOption,
        paymentToken: pending.paymentToken,
        creditCardFirst6: pending.creditCardFirst6,
        creditCardLast4: pending.creditCardLast4,
        expireMonth: pending.expireMonth,
        expireYear: pending.expireYear,
        orderIDs: [orderID],
    });
    scope.payment.paymentOption = resp;
    delete scope.payment.pendingPaymentOptionCreate;
}

// DeleteSavedPaymentOption soft-deletes a PaymentOption (PATCH active:false
// for signed-in users, in-memory-only for guests), shows a toast with an
// Undo link, and refreshes the payment section.
export async function DeleteSavedPaymentOption(state: State, poID: string): Promise<void> {
    const upo = Array.from(state.userPaymentOptions).find(u => u.id === poID);
    if (!upo) {
        return;
    }

    if (state.sess?.user.id) {
        await axios.patch(`/payment-options/${poID}`, { 'active': false }).catch(() => undefined);
        toast.Undo('Deleted payment option.', async () => {
            await undoDelete(state, poID);
        });
    }

    state.userPaymentOptions.delete(upo);
    if (state.payment.paymentOption?.id === poID) {
        state.payment = { billingShippingSame: true };
    }
    await state.initPayment();
}

async function undoDelete(state: State, poID: string): Promise<void> {
    await axios.patch(`/payment-options/${poID}`, { 'active': true }).
        then(() => state.initPayment()).
        then(() => {
            toast.Undo('Reverted payment option.', async () => {
                await redoDelete(state, poID);
            });
        }).catch(ShowError);
}

async function redoDelete(state: State, poID: string): Promise<void> {
    await axios.patch(`/payment-options/${poID}`, { 'active': false }).
        then(() => state.initPayment()).
        then(() => {
            toast.Undo('Updated payment option.', async () => {
                await undoDelete(state, poID);
            });
        }).catch(ShowError);
}

// MarkPlusCardFieldInvalid paints a TokenizeFieldError onto the hidden
// validity-mirror input next to the PAN/CVC mount. Plus SCSS already turns
// the mount red and sizes the feedback pill off
// `.blockchyp-validity-mirror + .invalid-feedback` — reuse that, add no SCSS.
export function MarkPlusCardFieldInvalid(field: 'number' | 'cvv', message: string, root: ParentNode = document): void {
    const $wrapper = root.querySelector<HTMLElement>(field === 'number' ? '.cc-number' : '.cc-cvc');
    if (!$wrapper) {
        return;
    }
    const $mirror = $wrapper.nextElementSibling as HTMLElement | null;
    if (!$mirror?.classList.contains('blockchyp-validity-mirror')) {
        return;
    }
    $mirror.classList.add('is-invalid');
    let $message = $mirror.nextElementSibling;
    if (!$message?.classList.contains('invalid-feedback')) {
        $message = document.createElement('div');
        $message.classList.add('invalid-feedback');
        $mirror.parentElement?.insertBefore($message, $mirror.nextElementSibling);
    }
    $message.textContent = message;
    $wrapper.scrollIntoView({ block: 'center', behavior: 'smooth' });
}

// ClearPlusCardFieldInvalid reverts MarkPlusCardFieldInvalid. The manual
// `is-invalid` on the mirror installs no ReportValidity revert listener, so
// without this a corrected PAN would stay painted red when a later attempt
// fails on the CVC (or vice versa). Call before every tokenize attempt.
export function ClearPlusCardFieldInvalid(root: ParentNode = document): void {
    for (const selector of ['.cc-number', '.cc-cvc']) {
        const $wrapper = root.querySelector<HTMLElement>(selector);
        const $mirror = $wrapper?.nextElementSibling as HTMLElement | null;
        if (!$mirror?.classList.contains('blockchyp-validity-mirror')) {
            continue;
        }
        $mirror.classList.remove('is-invalid');
        const $message = $mirror.nextElementSibling;
        if ($message?.classList.contains('invalid-feedback')) {
            $message.remove();
        }
    }
}

export { DeletePaymentOption, NewPaymentOption };
